import { useEffect, useRef, useState } from "react";
import { apiClient } from "@/services/api";
import { useLatest } from "@/hooks/useLatest";
import type { VideoResolutionOption } from "@/types";

interface UseVideoPlaybackSourceResult {
  /** Signed playback URL for the selected resolution, or "" while unset. */
  playbackUrl: string;
  availableResolutions: VideoResolutionOption[];
  selectedResolution: string;
  setSelectedResolution: (resolution: string) => void;
  isLoadingResolutions: boolean;
  /**
   * True while `playbackUrl` still belongs to the previous selection, so the
   * element holds the previous clip's media and its events must be ignored.
   */
  isUrlSwitchPending: boolean;
}

const DEFAULT_RESOLUTION = "360p";

/**
 * Owns the legacy per-file MP4 fetch chain: resolutions for the selected
 * video, then the signed playback URL for the selected resolution. On the
 * HLS path (hlsSourceUrl set) the recording playlist is used directly and
 * both fetches are bypassed.
 */
export function useVideoPlaybackSource(
  selectedVideoId: string,
  hlsSourceUrl: string | null,
): UseVideoPlaybackSourceResult {
  const [currentVideoUrl, setCurrentVideoUrl] = useState("");
  const [availableResolutions, setAvailableResolutions] = useState<
    VideoResolutionOption[]
  >([]);
  const [selectedResolution, setSelectedResolution] =
    useState<string>(DEFAULT_RESOLUTION);
  const [isLoadingResolutions, setIsLoadingResolutions] = useState(false);
  // Latest selected id at completion time; requests that finish after the
  // user picked another clip are dropped instead of clobbering the new one.
  const selectedVideoIdRef = useLatest(selectedVideoId);
  // The video id that currentVideoUrl was fetched for.
  const loadedUrlVideoIdRef = useRef("");

  // Fetch available resolutions when video changes
  useEffect(() => {
    const fetchResolutions = async () => {
      if (hlsSourceUrl) {
        setAvailableResolutions([]);
        setSelectedResolution("auto");
        setIsLoadingResolutions(false);
        return;
      }
      if (!selectedVideoId) {
        setAvailableResolutions([]);
        return;
      }

      setIsLoadingResolutions(true);
      try {
        const resolutionData = await apiClient.getVideoResolutions(
          selectedVideoId,
        );
        setAvailableResolutions(resolutionData.resolutions);

        // Auto-select 360p if available, otherwise select first available resolution
        const preferred = resolutionData.resolutions.find(
          (r) => r.resolution === DEFAULT_RESOLUTION,
        );
        const resolution = preferred
          ? DEFAULT_RESOLUTION
          : resolutionData.resolutions[0]?.resolution || "original";
        setSelectedResolution(resolution);
      } catch (error) {
        console.error("Error fetching video resolutions:", error);
        setAvailableResolutions([]);
        setSelectedResolution("original");
      } finally {
        setIsLoadingResolutions(false);
      }
    };

    fetchResolutions();
  }, [hlsSourceUrl, selectedVideoId]);

  // Update video URL when resolution changes
  useEffect(() => {
    const updateVideoUrl = async () => {
      if (hlsSourceUrl) {
        loadedUrlVideoIdRef.current = "";
        setCurrentVideoUrl("");
        return;
      }
      if (!selectedVideoId || !selectedResolution) {
        loadedUrlVideoIdRef.current = "";
        setCurrentVideoUrl("");
        return;
      }

      const videoId = selectedVideoId;
      try {
        const playbackData = await apiClient.getVideoPlaybackUrl(
          videoId,
          selectedResolution,
        );
        if (selectedVideoIdRef.current !== videoId) {
          // A newer selection superseded this request; drop the stale URL.
          return;
        }
        loadedUrlVideoIdRef.current = videoId;
        setCurrentVideoUrl(playbackData.playbackUrl);
      } catch (error) {
        console.error("Error fetching video playback URL:", error);
        // Don't change the URL on error to avoid reloading
      }
    };

    updateVideoUrl();
  }, [hlsSourceUrl, selectedVideoId, selectedResolution, selectedVideoIdRef]);

  return {
    playbackUrl: currentVideoUrl,
    availableResolutions,
    selectedResolution,
    setSelectedResolution,
    isLoadingResolutions,
    isUrlSwitchPending:
      currentVideoUrl !== "" &&
      loadedUrlVideoIdRef.current !== selectedVideoId,
  };
}
